import React, { useEffect, useState, useRef } from "react";
import { createPost, deployBlog, uploadAndNormalizeUrl, uploadImage, uploadImgByUrl, useBlogMeta, type NewPost } from "./api";
import { CheckCircle2Icon, ImagePlusIcon, PlusIcon, X } from "lucide-react";
import { toTranslit, debounce, saveFileToOPFS, getFileFromOPFS, deleteFileFromOPFS } from "./utils";
import { baseEditorConfig } from "./editor";
import EditorJS, { type OutputData } from "@editorjs/editorjs";
import { useBlogStore } from "./store";
import { AddCategoryModal } from "./components/AddCategoryModal";
import { Button } from "./components/Button";
import { Selector } from "./components/Selector";
import { useNavigate } from "react-router-dom";
import DatePicker from "react-datepicker";
// @ts-ignore
import "react-datepicker/dist/react-datepicker.css";
import { useFormWithDraft } from "./use-draft-form";
import { Controller } from "react-hook-form";
import { availableLanguages } from "../shared";

interface PostFormValues {
  id: number;
  title: string;
  slug: string;
  metaTitle: string;
  metaDescription: string;
  previewImageAlt: string;
  blog: string;
  category: string;
  author: string;
  language: NonNullable<NewPost["language"]>;
  createdAt: Date | string;
  content: OutputData["blocks"] | null;
  previewImage: string | null;
  previewImageFile?: string | null;
}

export function CreatePost() {
  const navigate = useNavigate();
  const { selectedBlog } = useBlogStore();

  const {
    register,
    control,
    handleSubmit,
    setValue,
    getValues,
    watch,
    reset,
    loading: draftLoading
  } = useFormWithDraft<PostFormValues>(`${selectedBlog}-new-post`, {
    defaultValues: {
      title: "",
      slug: "",
      metaTitle: "",
      metaDescription: "",
      previewImageAlt: "",
      blog: selectedBlog,
      category: "",
      author: "",
      language: "en" as const,
      createdAt: new Date(),
      content: null,
      previewImage: null,
      previewImageFile: null
    }
  });

  const formValues = watch();

  const onSuccess = (data: any) => {
    const { authors, categories } = data;
    const currentValues = getValues();

    if (authors.length && !currentValues.author) {
      setValue("author", authors[0].slug);
    }
    if (categories.length && !currentValues.category) {
      setValue("category", categories[0].slug);
    }
  };
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);

  const { data = { authors: [], categories: [] }, loading: metaLoading, refetch: refetchMeta } = useBlogMeta(selectedBlog, { onSuccess });

  const handleCategoryAdded = async (newCategory: { slug: string; title: string }) => {
    setValue("category", newCategory.slug);
    refetchMeta?.();
  };

  // Only infer the slug from the title while the slug is still empty, so a manually set one is never overwritten
  const inferSlugFromTitle = (title: string) => {
    if (getValues("slug")?.trim()) return;
    setValue("slug", toTranslit(title.trim()));
  };

  const [action, setAction] = useState<"publishing" | "saving" | "done" | null>(null);
  const [showMessage, setShowMessage] = useState(false);

  const [tab, setTab] = useState<"content" | "meta">("content");
  // We still need local state for the file object as it can't be stored in draft
  const [previewImageFile, setPreviewImageFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{
    title: string;
    content: string;
    image: string;
  } | null>(null);

  const editorRef = useRef<EditorJS | null>(null);

  // TODO rework validation
  const validate = async (publish = false) => {
    const newErrors = { title: "", content: "", image: "" };

    if (!formValues.title.trim()) {
      newErrors.title = publish ? "Title is required to publish" : "Title is required to save";
    }

    if (publish) {
      if (!formValues.previewImage) {
        newErrors.image = "Preview image is required to publish";
      }

      try {
        const content = await editorRef.current?.save();
        if (!content?.blocks?.length) {
          newErrors.content = "Content is required to publish";
        }
      } catch {
        newErrors.content = "Content is required to publish";
      }
    }

    setErrors(newErrors);
    setTimeout(() => setErrors(null), 2000);

    return !newErrors.title && !newErrors.content && !newErrors.image;
  };
  function handleImageInputChange(target: React.ChangeEvent<HTMLInputElement>["target"]) {
    if (target.files && target.files[0]) {
      const file = target.files[0];
      handleImageFile(file);
    }
  }

  function handleImageFile(file: File) {
    setPreviewImageFile(file);
    const filename = `${Date.now()}-${file.name}`;
    saveFileToOPFS(file, filename).then(() => {
      setValue("previewImageFile", filename);
      setValue("previewImage", URL.createObjectURL(file));
    });
  }

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const { files } = e.dataTransfer;

    // Native file drop
    if (files && files.length && files[0]?.type.startsWith("image/")) {
      handleImageFile(files[0]);
      return;
    }

    // URL drop
    const url = e.dataTransfer.getData("text/uri-list") || e.dataTransfer.getData("text/plain");
    if (!url || !url.startsWith("http")) return;

    setValue("previewImage", url);
  };

  const onSubmit = async (data: PostFormValues, publish = false) => {
    if (!selectedBlog) return;

    setAction(publish ? "publishing" : "saving");
    const content = await editorRef.current?.save();
    if (!content || !content.blocks) return;

    const updatedBlocks = await Promise.all(
      content.blocks.map(async (block) => {
        // image block
        if (block.type === "image") {
          return {
            ...block,
            data: {
              ...block.data,
              file: {
                ...block.data.file,
                url: await uploadAndNormalizeUrl(block.data.file.url)
              }
            }
          };
        }

        // banner block
        if (block.type === "banner" && block.data.image) {
          return {
            ...block,
            data: {
              ...block.data,
              image: { ...block.data.image, url: await uploadAndNormalizeUrl(block.data.image.url) }
            }
          };
        }

        return block;
      })
    );

    let finalPreviewImage = data.previewImage;

    if (previewImageFile) {
      const uploadRes = await uploadImage(previewImageFile);
      if (uploadRes.error) {
        console.error("Image upload failed:", uploadRes.error);
        setAction(null);

        return;
      }
      finalPreviewImage = `${window.location.origin}/${uploadRes.data?.path}`;
    }

    if (!previewImageFile && data.previewImage && !data.previewImage.startsWith(window.location.origin)) {
      const uploadRes = await uploadImgByUrl(data.previewImage);
      if (uploadRes.error) {
        console.error("Image upload failed:", uploadRes.error);
        setAction(null);

        return;
      }
      finalPreviewImage = `${window.location.origin}/${uploadRes.data?.path}`;
    }

    try {
      const createReturn = await createPost({
        ...data,
        content: updatedBlocks,
        previewImage: finalPreviewImage || "",
        createdAt: new Date(data.createdAt)
      });

      const [newPost] = createReturn.data || [];

      const newPostId = newPost?.id!;

      if (publish) await deployBlog(newPostId);
      else navigate(`/edit/${newPostId}`);

      setShowMessage(true);
      setTimeout(() => setShowMessage(false), 1500);
      // Clear draft and cleanup OPFS file
      if (formValues.previewImageFile) {
        deleteFileFromOPFS(formValues.previewImageFile);
      }
      reset();
      editorRef.current?.clear();
    } catch (err) {
      setAction(null);
      console.error(err);
    } finally {
      setAction("done");
    }
  };

  useEffect(() => {
    // Check if restored previewImage is a blob URL and validate it
    if (formValues.previewImage) {
      const img = new Image();
      img.onerror = () => {
        // Blob URL is invalid, clear it
        setValue("previewImage", null);
        setPreviewImageFile(null);
      };
      img.src = formValues.previewImage;

      return () => URL.revokeObjectURL(formValues.previewImage!);
    }
  }, [formValues.previewImage]);

  useEffect(() => {
    // Restore file from OPFS if we have a filename but no file object
    if (formValues.previewImageFile && !previewImageFile && formValues.previewImage?.startsWith("blob:")) {
      getFileFromOPFS(formValues.previewImageFile)
        .then((file) => {
          if (file) {
            setPreviewImageFile(file);
            setValue("previewImage", URL.createObjectURL(file));
          }
        })
        .catch(() => setValue("previewImageFile", null));
    }
  }, [formValues.previewImageFile, previewImageFile, formValues.previewImage, setValue]);

  useEffect(() => {
    if (!document.getElementById("editorjs") || metaLoading || draftLoading) return;

    editorRef.current = new EditorJS({
      ...baseEditorConfig,
      data: { blocks: formValues.content || undefined },
      onChange: debounce(async (api) => {
        const { blocks } = await api.saver.save();
        setValue("content", blocks, { shouldDirty: true });
      }, 500)
    } as any);

    return () => {
      if (editorRef.current) {
        editorRef.current?.destroy();
        editorRef.current = null;
      }
    };
  }, [draftLoading, metaLoading]);

  if (!selectedBlog) return <div>Missing Blog ID</div>;
  if (draftLoading || metaLoading)
    return (
      <div className='flex items-center justify-center h-full'>
        <span className='animate-spin rounded-full size-6 border-2 border-[#a0a0a0] border-t-transparent' />
      </div>
    );

  return (
    <>
      <div className=' w-full h-full relative'>
        <div
          className={`grid h-full grid-cols-[1fr_693px_331px_1fr] ${tab === "content" && "grid-rows-[100px_0fr_0fr_1fr]"} ${
            tab === "meta" && "grid-rows-[100px_1fr]"
          } `}
        >
          <div className='flex justify-center border-dashed border-b border-[#dedede]' />
          <div className='flex justify-between items-end p-2 border-dashed border-b border-x border-[#dedede] relative'>
            <div className='size-2.75 absolute -left-1.5 -bottom-1.5 flex items-center justify-center'>
              <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
              <div className='absolute h-2.75 z-10 w-px bg-[#A1A1A1]' />
            </div>
            <div className='size-2.75 absolute -right-1.5 -bottom-1.5 flex items-center justify-center'>
              <div className='absolute h-px w-2.75 bg-[#A1A1A1]' />
              <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
            </div>
            <div className='bg-[#e0e0e0] font-medium flex text-xs items-center rounded-lg shrink h-9 px-px border-[#e0e0e0] border-y'>
              <button
                className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                  tab === "content" && "bg-white shadow ring ring-neutral-950/10"
                }`}
                onClick={() => setTab("content")}
              >
                article content
              </button>
              <button
                className={`h-full px-3 transition-all ease-linear rounded-lg cursor-pointer uppercase font-semibold ${
                  tab === "meta" && "bg-white shadow ring ring-neutral-950/10"
                }`}
                onClick={() => setTab("meta")}
              >
                meta tags
              </button>
            </div>
            <div className='flex items-center flex-row gap-2'>
              <Button
                loading={action === "saving"}
                disabled={!!action}
                onClick={async () => {
                  const valid = await validate(false);
                  if (valid) handleSubmit((data) => onSubmit(data, false))();
                }}
                className='uppercase'
              >
                save draft
              </Button>

              <Button
                loading={action === "publishing"}
                disabled={!!action}
                onClick={async () => {
                  const valid = await validate(true);
                  if (valid) handleSubmit((data) => onSubmit(data, true))();
                }}
                className='uppercase'
              >
                save & publish
              </Button>
            </div>
          </div>
          <div className='flex items-end border-dashed border-b border-[#dedede] p-3 col-span-2'>
            {showMessage && (
              <div className='flex flex-row items-center gap-1 h-fit w-fit text-sm font-medium'>
                <CheckCircle2Icon
                  size={16}
                  className='text-green-600'
                />
                <span>Successfully saved! Article will appear on the site within 90 seconds</span>
              </div>
            )}
          </div>
          <div className='flex justify-center border-dashed border-b border-[#dedede]' />
          {tab === "meta" && (
            <>
              <div className='flex justify-center p-2 border-dashed border-x border-[#dedede] row-span-2'>
                <div className='bg-white w-full p-4 rounded-lg h-100'>
                  <div className='flex flex-col'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Article slug </span>•
                      <button
                        className='text-blue-500 text-xs font-semibold uppercase'
                        onClick={() => setValue("slug", toTranslit(formValues.title?.trim() || ""))}
                      >
                        generate
                      </button>
                    </div>
                    <input
                      className='unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8]'
                      {...register("slug")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("slug", input.value);
                      }}
                      type='text'
                    />
                  </div>
                  <div className='flex flex-col mt-8'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Meta title </span>

                      {formValues.metaTitle !== "" && (
                        <>
                          <span className='text-[#a0a0a0]'>•</span>
                          <span className='text-xs min-w-37.5 text-[#a0a0a0]'>{formValues.metaTitle?.length || 0} chars</span>
                        </>
                      )}
                    </div>
                    <input
                      className='unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8]'
                      {...register("metaTitle")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("metaTitle", input.value);
                      }}
                      type='text'
                    />
                  </div>
                  <div className='flex flex-col mt-5'>
                    <div className='flex items-center gap-1'>
                      <span className='text-xs uppercase font-semibold'> Meta description </span>

                      {formValues.metaDescription !== "" && (
                        <>
                          <span className='text-[#a0a0a0]'>•</span>
                          <span className='text-xs min-w-37.5 text-[#a0a0a0]'>{formValues.metaDescription?.length || 0} chars</span>
                        </>
                      )}
                    </div>
                    <textarea
                      className='unset inline-flex items-center justify-center text-sm leading-none h-8 box-border rounded-sm p-2 border outline-none max-w-112.5 w-full border-[#e0e1e2] mt-0.5 bg-[#f8f8f8] min-h-18'
                      {...register("metaDescription")}
                      onPaste={(e) => {
                        const input = e.target as HTMLInputElement;
                        setValue("metaDescription", input.value);
                      }}
                    />
                  </div>
                </div>
              </div>
              <div className='flex justify-center' />
            </>
          )}
          {tab === "content" && (
            <>
              <div className='flex justify-center border-dashed border-x border-[#dedede] border-b relative'>
                <div className='size-2.75 absolute -left-px -bottom-1.5 flex items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1] z-10' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
                </div>
                <div className='size-2.75 absolute -right-px -bottom-1.5 flex justify-end items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1] z-10' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1] z-10' />
                </div>
                <div className='flex flex-col w-full p-2'>
                  <textarea
                    {...register("title", {
                      onChange: (e) => inferSlugFromTitle(e.target.value)
                    })}
                    rows={1}
                    className={`unset text-sm leading-none box-border h-auto p-4 border outline-none w-full font-bold text-[24px] text-[#21272e] bg-white border-[#e0e0e0] rounded-lg resize-none overflow-hidden placeholder:text-[#c0c0c0] ${
                      errors && errors.title && "border-red-500"
                    }`}
                    placeholder='Article title'
                    onPaste={(e) => {
                      const textarea = e.target as HTMLTextAreaElement;
                      const title = textarea.value;
                      setValue("title", title);
                      inferSlugFromTitle(title);
                    }}
                  />

                  {errors && errors.title && <span className='text-xs text-red-500 mt-1'>{errors?.title}</span>}
                </div>
              </div>

              <div className='flex flex-col gap-2 p-2 group row-span-2'>
                {/* Date picker */}
                <Controller
                  control={control}
                  name='createdAt'
                  render={({ field }) => (
                    <DatePicker
                      customInput={
                        <div className='flex flex-row gap-1 items-center w-68 border-solid border-[#e0e0e0] bg-white rounded-lg border px-2 h-10 cursor-pointer hover:border-[#a0a0a0] transition-colors'>
                          <span className='text-gray-500 text-xs uppercase ml-1'>Date:</span>

                          <span className='text-sm text-[#21272e]'>
                            {field.value ? (
                              (() => {
                                const now = new Date();
                                const date = new Date(field.value);
                                if (date.getFullYear() === now.getFullYear()) {
                                  return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
                                }
                                return date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
                              })()
                            ) : (
                              <span className='text-[#a0a0a0]'>--</span>
                            )}
                          </span>
                        </div>
                      }
                      selected={field.value ? new Date(field.value) : null}
                      onChange={(date: Date | null) => field.onChange(date)}
                      todayButton='Today'
                      dateFormat='MMM d, yyyy'
                      dropdownMode='select'
                      calendarClassName='custom-datepicker'
                      wrapperClassName='datepicker-toggle-close'
                      popperClassName='custom-datepicker-popper'
                      popperPlacement='bottom-start'
                    />
                  )}
                />

                {/* Category Selector */}
                <div className='flex flex-row gap-1'>
                  <Selector
                    label='Category'
                    value={formValues.category || ""}
                    onChange={(value) => setValue("category", value)}
                    options={
                      data?.categories.map((category) => ({
                        value: category.slug,
                        label: category.title
                      })) || []
                    }
                  />
                  <Button
                    variant='icon'
                    onClick={() => setIsCategoryModalOpen(true)}
                    className='size-10 opacity-0 group-hover:opacity-100 transition-opacity'
                  >
                    <PlusIcon
                      size={18}
                      color='#a0a0a0'
                    />
                  </Button>
                </div>
                {/* Author Selector */}
                <Selector
                  label='Author'
                  value={formValues.author || ""}
                  onChange={(value) => setValue("author", value)}
                  options={
                    data?.authors.map((author) => ({
                      value: author.slug,
                      label: author.name
                    })) || []
                  }
                />

                {/* Language Selector  */}
                <Selector
                  label='Language'
                  value={formValues.language || ""}
                  onChange={(value) => setValue("language", value as NonNullable<NewPost["language"]>)}
                  options={availableLanguages}
                />
              </div>

              <div className='flex justify-center' />

              <div className='flex justify-center' />
              <div className='flex flex-col border-dashed border-x border-[#dedede] border-b relative'>
                <div className='size-2.75 absolute -left-px -bottom-1.5 flex items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1]' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1]' />
                </div>
                <div className='size-2.75 absolute -right-px -bottom-1.5 flex justify-end items-center'>
                  <div className='absolute h-px w-1.5 bg-[#A1A1A1]' />
                  <div className='absolute h-2.75 w-px bg-[#A1A1A1]' />
                </div>
                <div className='p-2'>
                  {formValues.previewImage ? (
                    <div
                      className='flex flex-col gap-2 w-168.75'
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className='relative group'>
                        <img
                          src={formValues.previewImage}
                          alt={formValues.previewImageAlt || ""}
                          className='rounded-lg w-full'
                        />
                        <button
                          onClick={(e) => {
                            e.preventDefault();
                            setValue("previewImage", null);
                            setPreviewImageFile(null);
                            setValue("previewImageAlt", "");

                            // Clean up OPFS file
                            if (formValues.previewImageFile) {
                              deleteFileFromOPFS(formValues.previewImageFile);
                              setValue("previewImageFile", null);
                            }
                          }}
                          className='absolute top-2 right-2 p-1.5 bg-white/90 hover:bg-white rounded-full shadow-sm cursor-pointer transition-all opacity-0 group-hover:opacity-100'
                        >
                          <X
                            size={16}
                            className='text-gray-600'
                          />
                        </button>
                      </div>
                      <input
                        type='text'
                        placeholder='Alt text'
                        className='unset inline-flex items-center justify-center text-sm leading-none h-9 box-border rounded-md px-3 border outline-none w-full text-[#21272e] border-[#e0e1e2] bg-white wrap-break-words placeholder:text-[#c0c0c0]'
                        {...register("previewImageAlt")}
                      />
                    </div>
                  ) : (
                    <label
                      htmlFor='file-upload'
                      className={`border border-[#e0e0e0] cursor-pointer flex p-2 leading-4 whitespace-pre rounded-lg bg-white  text-xs text-gray-700 w-full h-79.5 ${
                        errors && errors.image && "border-red-500"
                      }`}
                      onDragOver={handleDragOver}
                      onDrop={handleDrop}
                    >
                      <div className='border border-dashed w-full h-full flex-col flex items-center justify-center border-[#e0e0e0] rounded-sm'>
                        <ImagePlusIcon
                          size={40}
                          color='#a0a0a0'
                        />
                        <p className='mt-6 text-[14px] uppercase font-medium text-[#a0a0a0]'>Choose article cover or drag it here</p>
                      </div>
                    </label>
                  )}
                  <input
                    type='file'
                    id='file-upload'
                    className='hidden'
                    onChange={({ target }) => handleImageInputChange(target)}
                  />
                  {errors && errors.image && <span className='text-xs text-red-500 mt-1'>{errors.image}</span>}
                </div>
              </div>
              <div className='flex justify-center' />
              <div className='flex justify-center' />
            </>
          )}
          {/* Editor moved outside conditional blocks to prevent destruction on tab change */}
          <div className={`flex justify-center border-dashed border-x border-[#e0e0e0]  ${tab !== "content" && "hidden"}`}>
            <div className='flex w-full'>
              <div className='flex flex-col flex-1 p-2'>
                <div
                  id='editorjs'
                  className={`w-full text-sm min-h-100 box-border rounded-smborder border border-[#e0e0e0] rounded-lg bg-white outline-none ${
                    errors && errors.content && "border-red-500"
                  }`}
                />
                {errors && errors.content && <span className='text-xs text-red-500 mt-1'>{errors.content}</span>}
              </div>
            </div>
          </div>
          <div className={`flex justify-center ${tab !== "content" && "hidden"}`} />
          <div className={`flex justify-center ${tab !== "content" && "hidden"}`} />
        </div>
      </div>

      <AddCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => setIsCategoryModalOpen(false)}
        onCategoryAdded={handleCategoryAdded}
      />
    </>
  );
}
